import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useDashboardData } from './hooks/useDashboardData';
import { useNavbarSlot } from '@/contexts/NavbarSlot.context';
import { mergeReportFiltersIntoSearch } from '@/utils/reportFiltersUrl';
import { appRoutes } from '@routes';
import { SingleDatePicker } from '@/components/SingleDatePicker';
import { Dropdown, filterBarSelectStyles } from '@/components/Dropdown';
import { OverallProductivity } from './components/OverallProductivity.component';
import { KeyHighlights } from './components/KeyHighlights.component';
import { ProductivityOverTime } from './components/ProductivityOverTime.component';
import { ActivityBreakdown } from './components/ActivityBreakdown.component';
import { ActivityHeatmap } from './components/ActivityHeatmap.component';
import { TopApplications } from './components/TopApplications.component';
import { FocusVsDistraction } from './components/FocusVsDistraction.component';
import { RecentAlerts } from './components/RecentAlerts.component';
import { AttendanceSummary } from './components/AttendanceSummary.component';
import styles from './DashboardOverviewNew.module.scss';
import { IS_DEMO_MODE } from '@/utils/demoMode';
import {
  DEMO_OVERALL_PRODUCTIVITY_DATA,
  DEMO_KEY_HIGHLIGHTS_DATA,
  DEMO_PRODUCTIVITY_OVER_TIME_DATA,
  DEMO_CHART_TICKS,
  DEMO_ACTIVITY_BREAKDOWN_DATA,
  DEMO_ACTIVITY_HEATMAP_DATA,
  DEMO_TOP_APPLICATIONS_DATA,
  DEMO_FOCUS_VS_DISTRACTION_DATA,
  DEMO_RECENT_ALERTS_DATA,
  DEMO_ATTENDANCE_SUMMARY_DATA,
  DEMO_MEMBERS_OPTIONS,
  DEMO_SELECTED_MEMBER,
} from './DashboardOverviewNew.demoData';

export const DashboardOverviewNewComponent = () => {
  const {
    dateRange,
    setDateRange,
    membersOptions,
    selectedMember,
    setSelectedMember,
    isLoadingMembers,
    isFetchingStats,
    isFetchingAlerts,
    overallProductivityData,
    keyHighlightsData,
    activityBreakdownData,
    focusVsDistractionData,
    topApplicationsData,
    recentAlertsData,
    productivityOverTimeData,
    chartTicks,
    activityHeatmapData,
    attendanceSummaryData,
  } = useDashboardData();

  const setNavbarSlot = useNavbarSlot();
  const location = useLocation();

  // Demo mode has no real backend data behind the fake demo profile — every
  // Firestore-backed hook returns empty/zero. Override with fixed demo
  // constants regardless of the currently selected date/member, and force
  // loading flags off so the navbar-slot pickers never leave the page stuck
  // on a spinner or empty state.
  const displayMembersOptions = IS_DEMO_MODE
    ? DEMO_MEMBERS_OPTIONS
    : membersOptions;
  const displaySelectedMember = IS_DEMO_MODE
    ? selectedMember || DEMO_SELECTED_MEMBER
    : selectedMember;
  const displayIsLoadingMembers = IS_DEMO_MODE ? false : isLoadingMembers;
  const displayIsFetchingStats = IS_DEMO_MODE ? false : isFetchingStats;
  const displayIsFetchingAlerts = IS_DEMO_MODE ? false : isFetchingAlerts;
  const displayOverallProductivityData = IS_DEMO_MODE
    ? DEMO_OVERALL_PRODUCTIVITY_DATA
    : overallProductivityData;
  const displayKeyHighlightsData = IS_DEMO_MODE
    ? DEMO_KEY_HIGHLIGHTS_DATA
    : keyHighlightsData;
  const displayActivityBreakdownData = IS_DEMO_MODE
    ? DEMO_ACTIVITY_BREAKDOWN_DATA
    : activityBreakdownData;
  const displayFocusVsDistractionData = IS_DEMO_MODE
    ? DEMO_FOCUS_VS_DISTRACTION_DATA
    : focusVsDistractionData;
  const displayTopApplicationsData = IS_DEMO_MODE
    ? DEMO_TOP_APPLICATIONS_DATA
    : topApplicationsData;
  const displayRecentAlertsData = IS_DEMO_MODE
    ? DEMO_RECENT_ALERTS_DATA
    : recentAlertsData;
  const displayProductivityOverTimeData = IS_DEMO_MODE
    ? DEMO_PRODUCTIVITY_OVER_TIME_DATA
    : productivityOverTimeData;
  const displayChartTicks = IS_DEMO_MODE ? DEMO_CHART_TICKS : chartTicks;
  const displayActivityHeatmapData = IS_DEMO_MODE
    ? DEMO_ACTIVITY_HEATMAP_DATA
    : activityHeatmapData;
  const displayAttendanceSummaryData = IS_DEMO_MODE
    ? DEMO_ATTENDANCE_SUMMARY_DATA
    : attendanceSummaryData;

  const attendanceReportSearch = useMemo(
    () =>
      mergeReportFiltersIntoSearch(location.search, {
        memberId: displaySelectedMember?.value,
        start: dateRange.start,
        end: dateRange.end,
      }),
    [
      location.search,
      displaySelectedMember?.value,
      dateRange.start,
      dateRange.end,
    ]
  );

  // Alerts has no date filter, so it only needs the selected member forwarded.
  const alertsReportSearch = useMemo(
    () =>
      mergeReportFiltersIntoSearch('', {
        memberId: displaySelectedMember?.value,
      }),
    [displaySelectedMember?.value]
  );

  const handleDateChange = useCallback(
    (dateStr) => setDateRange({ start: dateStr, end: dateStr }),
    [setDateRange]
  );

  useEffect(() => {
    setNavbarSlot(
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '220px' }}>
          <Dropdown
            options={displayMembersOptions}
            value={displaySelectedMember}
            onChange={setSelectedMember}
            isLoading={displayIsLoadingMembers}
            isDisabled={displayIsLoadingMembers}
            placeholder="Select member..."
            selectStyles={filterBarSelectStyles}
          />
        </div>
        <SingleDatePicker
          date={dateRange.start}
          onDateChange={handleDateChange}
        />
      </div>
    );
    return () => setNavbarSlot(null);
  }, [
    dateRange.start,
    displayMembersOptions,
    displaySelectedMember,
    displayIsLoadingMembers,
    handleDateChange,
    setSelectedMember,
    setNavbarSlot,
  ]);

  return (
    <div className={styles.pageContainer}>
      <div className={styles.row}>
        <OverallProductivity
          data={displayOverallProductivityData}
          isLoading={displayIsFetchingStats}
        />
        <KeyHighlights
          data={displayKeyHighlightsData}
          isLoading={displayIsFetchingStats}
        />
      </div>

      <div className={styles.row2}>
        <ProductivityOverTime
          data={displayProductivityOverTimeData}
          isLoading={displayIsFetchingStats}
          chartTicks={displayChartTicks}
        />
        <ActivityBreakdown
          data={displayActivityBreakdownData}
          isLoading={displayIsFetchingStats}
          footerTo={{
            pathname: appRoutes().timeline,
            search: attendanceReportSearch,
          }}
        />
        <ActivityHeatmap
          data={displayActivityHeatmapData}
          isLoading={displayIsFetchingStats}
        />
      </div>

      <div className={styles.row3}>
        <TopApplications
          data={displayTopApplicationsData}
          isLoading={displayIsFetchingStats}
        />
        <FocusVsDistraction
          data={displayFocusVsDistractionData}
          isLoading={displayIsFetchingStats}
        />
        <RecentAlerts
          data={displayRecentAlertsData}
          isLoading={displayIsFetchingAlerts}
          reportFiltersSearch={alertsReportSearch}
        />
        <AttendanceSummary
          data={displayAttendanceSummaryData}
          isLoading={displayIsFetchingStats}
          reportFiltersSearch={attendanceReportSearch}
          className={styles.quarterCard}
        />
      </div>
    </div>
  );
};
